import { SurveyAnswers, SurveyAnswerValue, SurveyCard, SurveyCell, SurveyCellError, SurveyResponse } from "types/surveyTest.types"

/** A screen the session accepted: what the Answered tab lists and the Routing tab groups by. */
export interface AcceptedScreen {
  /** The step the screen showed at */
  step: number
  /** The screen's questions and their loop iterations */
  title: string
  /** The cards as they showed: the Answered tab names each answer by their texts */
  cards: SurveyCard[]
  /** The answers the screen was accepted with */
  values: SurveyAnswers
  /** The length of the routing trace when the screen showed: the decisions that led to it end there */
  traceLength: number
}

/** The test the drawer runs: its session, the session's latest response, and the screens accepted so far. */
export interface TestState {
  session: string
  response: SurveyResponse
  history: AcceptedScreen[]
}

/** "Brand rating (Alpha)" */
export const cardTitle = (card: SurveyCard): string =>
  card.loop.length ? `${card.question} (${card.loop.map((l) => l.text || l.iteration).join(", ")})` : card.question

/** "Age + Panel only" */
export const pageTitle = (cards: SurveyCard[]): string => cards.map(cardTitle).join(" + ")

/** Every cell id a card answers: its cells and all their open texts. */
export const cardCells = (card: SurveyCard): string[] => card.cells.flatMap((c) => [c.id, ...Object.values(c.open)])

/** Absent, null (a cleared number box), an empty or whitespace text, or no option ticked. */
export const isBlank = (v: SurveyAnswerValue | undefined): boolean =>
  v === undefined ||
  v === null ||
  (typeof v === "string" && v.trim() === "") ||
  (Array.isArray(v) && v.length === 0)

/** The option ids an answer chooses: only single and multi cards have options; a typed text chooses none. */
const chosen = (card: SurveyCard, v: SurveyAnswerValue | undefined): string[] =>
  card.kind !== "single" && card.kind !== "multi" ? [] : typeof v === "string" ? [v] : Array.isArray(v) ? v : []

/** The open-text cell of an option, own keys only, so an id such as "constructor" finds nothing. */
export const openOf = (cell: SurveyCell, option: string): string | undefined =>
  Object.prototype.hasOwnProperty.call(cell.open, option) ? cell.open[option] : undefined

/** A cell's value as far as the cell still offers it: a choice keeps only shown options; none left is no value. */
const keptValue = (card: SurveyCard, cell: SurveyCell, v: SurveyAnswerValue | undefined): SurveyAnswerValue | undefined => {
  if (card.kind !== "single" && card.kind !== "multi") return v
  if (typeof v === "string") return cell.shown.includes(v) ? v : undefined
  if (!Array.isArray(v)) return v
  const shown = v.filter((o) => cell.shown.includes(o))
  return shown.length ? shown : undefined
}

/**
 * A form kept on a page the engine rebuilt: the values of the cells it still has, a choice only for the
 * options its cell now shows, and an open text only for an open option the cell still shows. So the
 * browser never holds, or sends, an option it does not show.
 */
export const formOnScreen = (screen: SurveyResponse["screen"], form: SurveyAnswers): SurveyAnswers => {
  const out: SurveyAnswers = {}
  for (const card of screen.cards)
    for (const cell of card.cells) {
      const v = keptValue(card, cell, form[cell.id])
      if (v !== undefined) out[cell.id] = v
      for (const option of cell.shown) {
        const open = openOf(cell, option)
        if (open !== undefined && form[open] !== undefined) out[open] = form[open]
      }
    }
  return out
}

/** The answers to send: only the current screen's cells, and an open text only for a chosen open option. */
export const answersToSend = (screen: SurveyResponse["screen"], values: SurveyAnswers): SurveyAnswers => {
  const out: SurveyAnswers = {}
  for (const card of screen.cards)
    for (const cell of card.cells) {
      const v = values[cell.id]
      if (isBlank(v)) continue
      out[cell.id] = v
      for (const option of chosen(card, v)) {
        const open = openOf(cell, option)
        if (open !== undefined && !isBlank(values[open])) out[open] = values[open]
      }
    }
  return out
}

/** The engine's message for a required cell left blank. */
const requiredMessage = (card: SurveyCard): string =>
  card.kind === "single"
    ? "Choose an answer."
    : card.kind === "multi"
      ? "Tick at least one option."
      : card.kind === "text"
        ? "Type a response."
        : "Enter an answer."

/** Convenience checks from `rules` (the engine decides): required cells and whole numbers. */
export const convenienceErrors = (screen: SurveyResponse["screen"], values: SurveyAnswers): SurveyCellError[] => {
  const errors: SurveyCellError[] = []
  for (const card of screen.cards)
    for (const cell of card.cells) {
      const v = values[cell.id]
      if (isBlank(v)) {
        if (card.rules.required) errors.push({ cell: cell.id, rule: "required", message: requiredMessage(card) })
        continue
      }
      if (card.rules.whole && typeof v === "number" && !Number.isInteger(v)) {
        errors.push({ cell: cell.id, rule: "whole", message: "Enter a whole number." })
        continue
      }
      if (!card.rules.required) continue
      for (const option of chosen(card, v)) {
        const open = openOf(cell, option)
        if (open !== undefined && isBlank(values[open]))
          errors.push({ cell: open, rule: "required", message: "Type a response." })
      }
    }
  return errors
}

/** The same answers: the same cells, each with the same value, a multi's options in the same order. */
const sameAnswers = (a: SurveyAnswers, b: SurveyAnswers): boolean => {
  const keys = Object.keys(a)
  return (
    keys.length === Object.keys(b).length &&
    keys.every((k) => {
      const x = a[k]
      const y = b[k]
      return Array.isArray(x) && Array.isArray(y) ? x.length === y.length && x.every((v, i) => v === y[i]) : x === y
    })
  )
}

/**
 * The part of a screen the browser checks before Next. A page shown again (revision above 0) was rebuilt
 * on the values it echoed: once a card's answer differs from them, the cards after it may no longer apply,
 * so the check stops at that card, and the engine decides about the rest, listing a value it no longer
 * needs in `ignored`. A page shown for the first time is checked whole.
 */
export const screenToCheck = (
  screen: SurveyResponse["screen"],
  values: SurveyAnswers,
  echoed: SurveyAnswers
): SurveyResponse["screen"] => {
  if (screen.revision === 0) return screen
  const alone = (card: SurveyCard) => ({ ...screen, cards: [card] })
  const changed = screen.cards.findIndex(
    (card) => !sameAnswers(answersToSend(alone(card), values), answersToSend(alone(card), echoed))
  )
  return changed < 0 ? screen : { ...screen, cards: screen.cards.slice(0, changed + 1) }
}

/**
 * After a response: a screen answered adds itself to the history, with the answers sent. It was answered
 * when the step advanced, or when the test ended from it (a fill's end or terminate, whose step is the one
 * after the last page it applied). A refused or re-shown screen, a language change and a resync add
 * nothing; a response for another session starts a new test. An end that comes without `answers` (the
 * same end in another language) keeps a fill's: they are cell and option ids, the same in every language.
 */
export const afterResponse = (state: TestState | null, sent: SurveyAnswers, r: SurveyResponse): TestState => {
  if (!state || state.session !== r.session) return { session: r.session, response: r, history: [] }
  const before = state.response
  const answered =
    before.status === "question" &&
    before.screen.cards.length > 0 &&
    (r.step > before.step || r.status !== "question")
  const kept =
    r.status !== "question" && r.answers === undefined && before.answers !== undefined
      ? { ...r, answers: before.answers }
      : r
  return {
    session: r.session,
    response: kept,
    history: answered
      ? [
          ...state.history,
          {
            step: before.step,
            title: pageTitle(before.screen.cards),
            cards: before.screen.cards,
            values: sent,
            traceLength: before.trace.length,
          },
        ]
      : state.history,
  }
}
